/**
 * AI-generated code, moderated by lzygmanski
 */
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';

import {Item} from '@/components/ui/item';
import {Typography} from '@/components/ui/typography';
import type {
  HealthLogMeasurement,
  HealthLogMetricDefinition,
} from '@/services/graphqlHooks';
import HealthLogValues from '@/shared/residents/components/HealthLogValues';
import healthLogMetrics from '@/translation/healthLogMetrics';
import {TIME_FORMATS} from '@/utils/timeFormats';

export type AmbulancePrintHealthMeasurementsProps = {
  measurements: HealthLogMeasurement[];
  metricDefinitions: HealthLogMetricDefinition[];
};

const AmbulancePrintHealthMeasurements = ({
  measurements,
  metricDefinitions,
}: AmbulancePrintHealthMeasurementsProps) => {
  const {t} = useTranslation();

  if (!measurements || measurements.length === 0) return null;

  const groupedMeasurements = measurements.reduce<
    Record<string, HealthLogMeasurement[]>
  >((acc, measurement) => {
    const metricId = measurement.metricId;
    if (!acc[metricId]) {
      acc[metricId] = [];
    }
    acc[metricId].push(measurement);
    return acc;
  }, {});

  Object.keys(groupedMeasurements).forEach(metricId => {
    groupedMeasurements[metricId].sort(
      (a, b) => dayjs(b.recordedAt).unix() - dayjs(a.recordedAt).unix()
    );
  });

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="h2" asChild className="text-lg font-semibold">
        <h2>{t('Health measurements')}</h2>
      </Typography>

      <div className="space-y-2">
        {Object.entries(groupedMeasurements).map(
          ([metricId, metricMeasurements]) => {
            const metricDefinition = metricDefinitions.find(
              m => m.id === metricId
            );

            return (
              <div key={metricId} className="space-y-2">
                {/* Combined header: Metric name (left) | Date + Values (right) */}
                <div className="grid grid-cols-[200px_1fr] gap-2">
                  <Item
                    variant="muted"
                    size="compact"
                    className="flex items-center"
                  >
                    <Typography className="font-semibold">
                      {healthLogMetrics[metricId] || metricId}
                    </Typography>
                  </Item>

                  <Item
                    variant="muted"
                    size="compact"
                    className="grid grid-cols-[200px_1fr] items-center gap-3"
                  >
                    <Typography className="font-semibold">
                      {t('Date')}
                    </Typography>
                    <Typography className="font-semibold">
                      {t('Values')}
                    </Typography>
                  </Item>
                </div>

                <div className="grid grid-cols-[200px_1fr] gap-2">
                  <div />
                  <div className="space-y-2">
                    {metricMeasurements.map((measurement, idx) => (
                      <Item
                        key={idx}
                        variant="subtle"
                        size="compact"
                        className="grid grid-cols-[200px_1fr] items-center gap-3 print:break-inside-avoid"
                      >
                        <Typography>
                          {dayjs(measurement.recordedAt).format(
                            TIME_FORMATS.dateTime
                          )}
                        </Typography>

                        <div className="flex items-center">
                          <HealthLogValues
                            measurement={measurement}
                            metricDefinition={metricDefinition}
                          />
                        </div>
                      </Item>
                    ))}
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};

export default AmbulancePrintHealthMeasurements;
